import { useLingui } from "@lingui/react/macro";
import type { ToolAuditEntry, ToolAuditOutput } from "@rakazo/contracts";
import { ChevronRight, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useSpaceRpc } from "../lib/space-rpc";
import { formatToolActivityDuration } from "../lib/tool-activity-view";

export function TerminalActivity({
  botId,
  runId,
  live,
}: {
  botId: string;
  runId: string;
  live: boolean;
}) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const [open, setOpen] = useState(live);
  const [data, setData] = useState<ToolAuditOutput>();
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const next = await rpc.runs.activity({ botId, runId });
        if (!cancelled) {
          setData(next);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
      if (!cancelled && live) timer = setTimeout(refresh, 3000);
    };
    void refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [rpc, botId, runId, open, live, retry]);
  const latest = data?.entries.at(-1);
  return (
    <section data-testid="terminal-activity" className="my-2 text-xs text-muted-foreground">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-9 max-w-full items-center gap-2 text-left hover:text-foreground"
      >
        <ChevronRight size={14} className={open ? "rotate-90" : ""} />
        {live ? (
          <LoaderCircle size={13} className="animate-spin motion-reduce:animate-none" />
        ) : null}
        <span>
          {live ? t`Working` : t`Activity`}
          {data ? ` · ${data.entries.length}` : ""}
          {live && latest ? ` · ${latest.label}` : ""}
        </span>
      </button>
      {open ? (
        <div className="ms-2 space-y-1 border-s border-border ps-4">
          {error ? (
            <button
              type="button"
              onClick={() => setRetry((n) => n + 1)}
              className="py-2 text-destructive"
            >{t`Could not load activity. Retry`}</button>
          ) : !data ? (
            <p className="py-2">{t`Loading…`}</p>
          ) : null}
          {data?.truncated ? <p>{t`Showing the latest recorded activity`}</p> : null}
          {data?.entries.map((entry) => (
            <ActivityEntry key={entry.id} entry={entry} />
          ))}
          {data && !data.entries.length ? (
            <p className="py-2">{t`No recorded tool activity`}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
function ActivityEntry({ entry }: { entry: ToolAuditEntry }) {
  const { t } = useLingui();
  const state =
    entry.outcome === "running"
      ? t`Running`
      : entry.outcome === "succeeded"
        ? t`Completed`
        : entry.outcome === "error"
          ? t`Failed`
          : entry.outcome === "paused"
            ? t`Waiting`
            : t`No completion recorded`;
  return (
    <details className="group/entry">
      <summary className="flex min-h-8 cursor-pointer list-none items-start gap-2 py-1">
        <ChevronRight size={12} className="mt-0.5 shrink-0 group-open/entry:rotate-90" />
        <span
          className={
            entry.outcome === "error"
              ? "text-destructive"
              : entry.outcome === "running"
                ? "text-warning"
                : ""
          }
        >
          {entry.outcome === "succeeded" ? "✓" : entry.outcome === "error" ? "!" : "·"}
        </span>
        <span className="min-w-0 flex-1 break-words">{entry.label}</span>
        <span className="shrink-0 tabular-nums">
          {formatToolActivityDuration(entry.durationMs)}
        </span>
      </summary>
      <div className="mb-3 ms-5 space-y-1 break-words border-s border-border ps-3">
        <p className="select-text text-foreground">{entry.name}</p>
        <p>
          {state} · {new Date(entry.startedAt).toLocaleTimeString()}
        </p>
        {entry.error ? (
          <pre className="whitespace-pre-wrap text-destructive">{entry.error}</pre>
        ) : null}
      </div>
    </details>
  );
}
